import { Stack, Modal, Pagination, createStyles, Alert, Skeleton } from '@mantine/core';
import { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from '@emotion/react';
import useTranslation from 'next-translate/useTranslation';

import repeat from '~/common/utils/repeat';
import { UserContext } from '~/components/providers/UserProvider';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import dt from '~/testing';
import { GroupEntryViewsProvider } from '~/domains/group/components/GroupEntryViewsProvider/GroupEntryViewsProvider';

import useMyContestEntries from './useMyContestEntries';
import MyEntriesContent from './MyEntriesContent';

const useStyles = createStyles((theme) => ({
  pagination: {
    background: theme.white,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ 20,
    width: '100%',
    position: 'fixed',
    bottom: 0,
    left: 0,
    zIndex: 9,
  },
}));

const ITEMS_COUNT = 10;

function getPagesCount(total: number) {
  return Math.ceil(total / ITEMS_COUNT);
}

type MyEntriesModalProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  initialTotal: number;
  fullContest: FullContest;
  myEntriesStatus: string;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

function MyEntriesModal({
  isOpen,
  onClose,
  fullContest,
  initialTotal,
  myEntriesStatus,
  openEntryNameEditModal,
}: MyEntriesModalProps) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const { classes } = useStyles();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  const { t } = useTranslation('contest');

  const { teamPicks, userEntries, defaultSlate } = useMyContestEntries({
    pageSize: ITEMS_COUNT,
    enabled: isOpen,
  });

  const isLoading = teamPicks.isLoading || userEntries.isLoading;
  const isError = teamPicks.isError || userEntries.isError;

  return (
    <Modal
      onClose={onClose}
      opened={isOpen}
      title={t('detail.entries.title', { status: myEntriesStatus })}
      size="lg"
      radius="md"
      fullScreen
      data-test-id={dt.contestDetail.section.myEntries.modal}
    >
      <Stack spacing={0} /* eslint-disable-line local-rules/use-theme-tokens */ mb={20}>
        {isLoading && !isError && (
          <Stack spacing="lg">
            {repeat(ITEMS_COUNT, (index) => (
              <Skeleton
                h={isTablet ? 122 : 81}
                w="100%"
                key={index}
                data-test-id={dt.contestDetail.section.myEntries.skeleton}
              />
            ))}
          </Stack>
        )}
        {!isLoading && isError && <Alert color="red">{t('detail.entries.error')}</Alert>}
        {!isLoading && !isError &&
          (user?.id ? (
            <GroupEntryViewsProvider
              entryIds={userEntries?.data?.data?.map(({ id }) => id) ?? []}
              userId={user.id}
            >
              <MyEntriesContent
                defaultSlate={defaultSlate?.slate}
                teamPicks={teamPicks?.data}
                fullContest={fullContest}
                entries={userEntries?.data?.data}
                openEntryNameEditModal={openEntryNameEditModal}
              />
            </GroupEntryViewsProvider>
          ) : (
            <MyEntriesContent
              defaultSlate={defaultSlate?.slate}
              teamPicks={teamPicks?.data}
              fullContest={fullContest}
              entries={userEntries?.data?.data}
              openEntryNameEditModal={openEntryNameEditModal}
            />
          ))}
        <Pagination
          total={getPagesCount(userEntries?.data?.total ?? initialTotal)}
          page={userEntries.currentPage}
          onChange={userEntries.onCurrentPageChange}
          withEdges
          size="sm"
          siblings={4}
          disabled={isLoading}
          className={classes.pagination}
        />
      </Stack>
    </Modal>
  );
}

export default MyEntriesModal;
